/**
 * Mobile Calendar Handlers Hook
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Handles calendar date selection and month navigation
 */

import { useCallback } from "react";
import { TransactionCustomFieldValues } from "../../../../types";

// Local types matching CreateTransactionMobile.tsx
interface FormData {
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: any;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  feeAmount?: string;
  feeAccountId?: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

export type FocusedField = "amount" | "date" | "time" | "account" | "currency" | "category" | "fromAccount" | "toAccount" | "rate" | "defaultCurrencyRate" | "feeAccount" | "feeAmount" | `detail:${string}` | null;

export interface UseMobileCalendarProps {
  transactionType: "expense" | "income" | "transfer";
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  transferFormData: TransferFormData;
  setTransferFormData: React.Dispatch<React.SetStateAction<TransferFormData>>;
  calendarViewDate: Date;
  setCalendarViewDate: React.Dispatch<React.SetStateAction<Date>>;
  keepTimePickerOpen: boolean;
  setFocusedField: (field: FocusedField) => void;
  formatDateLocal: (date: Date) => string;
}

export function useMobileCalendar({
  transactionType,
  formData,
  setFormData,
  transferFormData,
  setTransferFormData,
  calendarViewDate,
  setCalendarViewDate,
  keepTimePickerOpen,
  setFocusedField,
  formatDateLocal,
}: UseMobileCalendarProps) {
  const handleCalendarDateSelect = useCallback((date: Date) => {
    const dateString = formatDateLocal(date);
    if (transactionType === "transfer") {
      setTransferFormData({ ...transferFormData, date: dateString });
    } else {
      setFormData({ ...formData, date: dateString });
    }
    // If time picker should stay open, switch to time field instead of closing
    if (keepTimePickerOpen) {
      setFocusedField("time");
    } else {
      // If amount is 0 or empty, go to amount so user enters it next; otherwise go to account
      const currentAmount = transactionType === "transfer" ? transferFormData.amount : formData.amount;
      const hasAmount = currentAmount != null && currentAmount.trim() !== "" && currentAmount.trim() !== "0";
      if (!hasAmount) {
        setFocusedField("amount");
      } else if (transactionType === "transfer") {
        setFocusedField("fromAccount");
      } else {
        setFocusedField("account");
      }
    }
  }, [transactionType, formData, setFormData, transferFormData, setTransferFormData, keepTimePickerOpen, setFocusedField, formatDateLocal]);

  const handleCalendarMonthChange = useCallback((direction: "prev" | "next") => {
    const newDate = new Date(calendarViewDate);
    if (direction === "prev") {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCalendarViewDate(newDate);
  }, [calendarViewDate, setCalendarViewDate]);

  return {
    handleCalendarDateSelect,
    handleCalendarMonthChange,
  };
}
